import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import ActivationModal from '../components/ActivationModal';
import { Alert, Button, Card, Container, Textarea } from '../components/site/ui';
import { useToast } from '../components/admin/overlays';
import SiteCredit from '../components/SiteCredit';
import {
  IconArrowRight,
  IconBan,
  IconCheck,
  IconChevronDown,
  IconClock,
  IconCourier,
  IconDownload,
  IconLock,
  IconMail,
  IconNote,
  IconPackage,
  IconRefresh,
  IconUser
} from '../components/icons';

const issueTypeLabels = {
  not_arrived: 'Not arrived yet',
  delivered_not_received: 'Delivered but not received',
  damaged: 'Damaged',
  missing_items: 'Missing items',
  unsafe_place: 'Left in unsafe place'
};

/**
 * A success toast for an action that reloads the page has nowhere to live —
 * the reload wipes it before it can be seen. Stashed here and read back once
 * on the next mount instead of being shown before the reload happens.
 */
const PENDING_TOAST_KEY = 'pb.merchantCase.pendingToast';

const stashToastAcrossReload = (message, tone = 'success') => {
  try {
    sessionStorage.setItem(PENDING_TOAST_KEY, JSON.stringify({ message, tone }));
  } catch {
    /* sessionStorage can throw in a locked-down/private browsing context —
       the reload still happens, only the confirmation toast is lost. */
  }
};

const formatDateTime = (value) =>
  new Date(value).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

const StatusPill = ({ tone, children }) => {
  const tones = {
    success: 'bg-success-50 text-success-700 ring-success-100',
    warning: 'bg-warning-50 text-warning-700 ring-warning-100',
    danger: 'bg-danger-50 text-danger-700 ring-danger-100',
    info: 'bg-info-50 text-info-700 ring-info-100'
  };
  const dots = {
    success: 'bg-success-600',
    warning: 'bg-warning-600',
    danger: 'bg-danger-600',
    info: 'bg-info-600'
  };
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-600 ring-1 ring-inset ${tones[tone]}`}
    >
      <span className={`h-2 w-2 rounded-full ${dots[tone]}`} aria-hidden="true" />
      {children}
    </span>
  );
};

const Accordion = ({ icon: Icon, title, open, onToggle, children }) => (
  <section>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className={`flex w-full items-center justify-between gap-3 border border-ink-200 bg-white px-4 py-3.5 text-left transition-colors hover:bg-ink-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 ${
        open ? 'rounded-t-xl' : 'rounded-xl'
      }`}
    >
      <span className="flex min-w-0 items-center gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
          <Icon size={17} />
        </span>
        <span className="font-display text-[15px] font-700 text-ink-900">{title}</span>
      </span>
      <IconChevronDown
        size={18}
        className={`shrink-0 text-ink-500 transition-transform ${open ? 'rotate-180' : ''}`}
      />
    </button>
    {open && (
      <div className="rounded-b-xl border border-t-0 border-ink-200 bg-white p-5">{children}</div>
    )}
  </section>
);

const MerchantCasePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [action, setAction] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState(false);
  const [payingNextCycle, setPayingNextCycle] = useState(false);
  const [trackingHistoryOpen, setTrackingHistoryOpen] = useState(false);
  const [deliveryDetailsOpen, setDeliveryDetailsOpen] = useState(true);
  // True for exactly one render: the moment we land back here from Stripe
  // Checkout. Blocks the normal case render (and the activation modal it
  // would otherwise show, still un-activated) until confirmCaseCheckout has
  // actually run — see the effect below for why this can't just wait on the
  // webhook.
  const [confirmingPayment, setConfirmingPayment] = useState(
    () => new URLSearchParams(window.location.search).get('payment') === 'success'
  );
  const m = caseData?.merchant?.merchantId;
  const activationStatus = m?.activationStatus;
  const freeCaseUsed = !!m?.freeCaseUsed;
  const awaitingActivation = activationStatus === 'awaiting_activation';
  const limited = activationStatus === 'limited';
  const isPayPerCase = caseData?.usage?.type === 'pay_per_case' || m?.billingModel === 'pay_per_case';
  // Pay-per-case is pay-as-you-go: this case needing payment is a property of
  // THIS case (never counted for billing), not of activationStatus —
  // awaiting_review only reflects whatever the merchant's last case did, so a
  // case opened before that happened would otherwise read as fully open.
  const payPerCaseNeedsPayment = isPayPerCase && !caseData?.billingCountedAt;
  // Same idea for subscription: the included-case allowance being spent is a
  // property of the CYCLE, read straight from usage.remaining rather than
  // activationStatus, which (like pay-per-case above) only reflects the
  // outcome of whatever case was acted on last, not this one.
  // `remaining` is null on an unlimited plan, and `null <= 0` is true in JS —
  // so without the typeof check every unlimited subscriber read as out of
  // cases and got the "pay for this case or upgrade" prompt immediately after
  // subscribing. Only a real number can mean the allowance is spent, which is
  // the same rule the server's subscriptionQuotaStatus applies.
  const quotaFilled =
    caseData?.usage?.type === 'subscription' &&
    typeof caseData.usage.remaining === 'number' &&
    caseData.usage.remaining <= 0 &&
    !caseData?.billingCountedAt;
  const awaitingReview = activationStatus === 'awaiting_review' || payPerCaseNeedsPayment || quotaFilled;
  const freeAndUsed = activationStatus === 'free' && freeCaseUsed;
  const canStartPilot = !!caseData?.canStartPilot;
  const pilotUsage = caseData?.usage?.type === 'pilot' ? caseData.usage : null;
  // Quota exhaustion never touches activationStatus (only the hourly sweep for
  // a lapsed time window does, via awaiting_activation) — so this is read
  // straight from the computed usage, not inferred from status.
  const pilotEnded = !!pilotUsage?.ended;
  // A merchant who has chosen nothing at all — no pilot, no plan, no
  // pay-per-case — is gated too, even on their very first case: picking the
  // free pilot is one click away in the same modal, but some choice is
  // required. The server enforces this the same way (caseAccessService); this
  // mirrors it for the UI rather than relying on a 403 to explain it.
  const gated =
    (awaitingActivation || limited || awaitingReview || freeAndUsed || pilotEnded || canStartPilot) &&
    !caseData?.billingCountedAt;

  // One reason drives the modal: which one decides its copy. `limited` is
  // excluded — that has its own dedicated "Account limited" alert further
  // down with a Pay invoice button, not this chooser. A subscription
  // merchant's awaiting_review is also excluded — that's a cycle-level "pay
  // to continue" handled by the inline alert below, not a plan decision.
  // Pay-per-case's payPerCaseNeedsPayment gets its own reason ('payPerCase')
  // rather than folding into 'gated': it must tell a merchant who already
  // chose pay-per-case where they already are, not re-offer the full
  // first-time chooser as if they'd picked nothing.
  // Every reason left here is a hard block mirroring the real server-side
  // gate, so none of them are dismissable — closing the modal is not a way
  // to act without choosing something.
  const activationReason = limited
    ? null
    : pilotEnded && !caseData?.billingCountedAt
      ? 'pilotEnded'
      : (awaitingActivation || freeAndUsed) && !caseData?.billingCountedAt
        ? 'gated'
        : payPerCaseNeedsPayment
          ? 'payPerCase'
          : quotaFilled
            ? 'quotaFilled'
            : canStartPilot && !caseData?.billingCountedAt
              ? 'newMerchant'
              : null;
  const showActivationModal = activationReason != null;

  useEffect(() => {
    // Reset state when token changes
    setCaseData(null);
    setLoading(true);
    setError(null);
    setAction(null);
    setRejectionReason('');

    const fetchCase = async () => {
      try {
        const response = await api.get(`/api/merchants/case/${token}`);
        setCaseData(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Case not found or link expired');
        } else {
          setError('Error loading case');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchCase();
  }, [token]);

  // Runs once per real mount (not on every token change, since only a reload
  // — a real remount — is what stashes one) and fires whatever the previous
  // page load left for this reload to show.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(PENDING_TOAST_KEY);
      if (raw) {
        sessionStorage.removeItem(PENDING_TOAST_KEY);
        const { message, tone } = JSON.parse(raw);
        if (message) toast(message, { tone });
      }
    } catch {
      /* malformed or inaccessible storage — nothing to show, nothing to fix */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Returning from Stripe Checkout used to just re-render with whatever
  // activation state the page already had — the webhook that actually
  // applies the subscription/pay-per-case choice can lag by a few seconds,
  // or (a local dev environment with no `stripe listen` forwarding running)
  // never arrive at all, so a merchant who had genuinely just paid kept
  // seeing the activation prompt again. This confirms the exact session
  // directly instead of waiting on the webhook; confirmCaseCheckout is safe
  // to call even if the webhook also lands separately.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (payment !== 'success' && payment !== 'cancelled') return;

    const sessionId = params.get('session_id');
    // Strip the query string either way so a refresh doesn't re-run this.
    window.history.replaceState(null, '', window.location.pathname);

    if (payment === 'cancelled') {
      toast('Payment was cancelled.', { tone: 'info' });
      return;
    }

    if (!sessionId) {
      // A checkout session created before this fix won't carry one — nothing
      // to confirm synchronously here, fall back to whatever the webhook did.
      setConfirmingPayment(false);
      return;
    }

    api
      .post('/api/stripe/confirm-case-checkout', { token, sessionId })
      .then(({ data }) => {
        stashToastAcrossReload(
          data?.activated
            ? 'Payment confirmed — you can now take action on this case.'
            : 'Payment received.'
        );
      })
      .catch((err) => {
        console.error('Confirm checkout error:', err);
        stashToastAcrossReload(
          'Payment received, but we could not confirm it just now — refresh in a moment.',
          'error'
        );
      })
      .finally(() => window.location.reload());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAction = async (actionType) => {
    if (actionType === 'reject' && !rejectionReason.trim()) {
      toast('Please provide a reason for rejection.', { tone: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/api/merchants/case/${token}/action`, {
        action: actionType,
        rejectionReason: actionType === 'reject' ? rejectionReason : undefined
      });
      // The reload (so the page re-reads the case and shows the confirmed
      // action) would wipe a toast fired now before it could ever be seen —
      // stashed instead, for the reloaded page's mount effect to show.
      stashToastAcrossReload('Decision recorded. The customer has been notified.');
      window.location.reload();
    } catch (err) {
      const msg = err.response?.data?.error || 'Error taking action. Please try again.';
      toast(msg, { tone: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayNextCycle = async () => {
    setPayingNextCycle(true);
    try {
      const { data } = await api.post('/api/stripe/next-cycle-checkout', { token });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast('Could not start payment.', { tone: 'error' });
    } catch (err) {
      toast(err.response?.data?.error || 'Could not start payment. Please try again.', { tone: 'error' });
    } finally {
      setPayingNextCycle(false);
    }
  };

  const handlePayInvoice = async () => {
    setPayingInvoice(true);
    try {
      const { data } = await api.post('/api/stripe/overage-checkout', { token });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast('Could not start payment.', { tone: 'error' });
    } catch (err) {
      toast(err.response?.data?.error || 'Could not start payment. Please try again.', { tone: 'error' });
    } finally {
      setPayingInvoice(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      const url = `${API_BASE_URL}/api/pdf/merchant/${token}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          toast('Case not found or link expired', { tone: 'error' });
        } else if (response.status === 403) {
          const data = await response.json().catch(() => ({}));
          toast(data?.error || 'You cannot download the evidence pack right now.', { tone: 'error' });
        } else {
          toast('Failed to download PDF', { tone: 'error' });
        }
        return;
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `evidence-pack-${caseData?.caseId || 'case'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      toast('Error downloading PDF. Please try again.', { tone: 'error' });
    }
  };

  if (confirmingPayment) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50">
        <p className="text-sm text-ink-500">Confirming your payment…</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50">
        <p className="text-sm text-ink-500">Loading case…</p>
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50 px-4">
        <div className="w-full max-w-md rounded-2xl border border-ink-200 bg-white p-8 text-center shadow-card">
          <img src="/brand.png" alt="ParcelBuddy" className="mx-auto h-14 w-auto" />
          <h1 className="mt-6 font-display text-xl font-700 tracking-tight text-ink-900">
            Case not found
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-ink-600">
            {error || 'This case link is invalid or has expired.'}
          </p>
          <a
            href="/verify"
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-600 text-brand-700 underline hover:text-brand-800"
          >
            Verify a case
            <IconArrowRight size={15} />
          </a>
        </div>
      </div>
    );
  }

  const statusTone =
    caseData.status === 'merchant_responded'
      ? 'success'
      : limited
        ? 'danger'
        : awaitingReview
          ? 'info'
          : 'warning';

  const statusLabel =
    caseData.status === 'merchant_responded'
      ? 'Action taken'
      : limited
        ? 'Account limited'
        : awaitingReview
          ? 'Awaiting review'
          : pilotEnded
            ? 'Pilot ended'
            : (awaitingActivation || freeAndUsed)
              ? 'Awaiting activation'
              : 'Awaiting merchant review';

  const gateMessage = limited
    ? 'Settle any outstanding invoice to take action or download the evidence pack.'
    : awaitingReview
      ? 'Pay for the next cycle to take action or download the evidence pack.'
      : pilotEnded
        ? 'Your free pilot has ended. Choose a plan to take action or download the evidence pack.'
        : (awaitingActivation || freeAndUsed)
          ? 'Choose a plan to take merchant actions.'
          : 'This is a secure delivery issue case prepared by the customer with supporting evidence.';

  const summaryRows = [
    { icon: IconPackage, label: 'Merchant', value: caseData.deliveryDetails.merchantName },
    { icon: IconNote, label: 'Order', value: `#${caseData.deliveryDetails.orderNumber}` },
    {
      icon: IconCourier,
      label: caseData.deliveryDetails.courier,
      value: caseData.deliveryDetails.trackingNumber
    },
    { icon: IconClock, label: 'Submitted', value: formatDateTime(caseData.submittedAt) }
  ];

  return (
    <div className="min-h-screen bg-ink-50">
      {/* Header */}
      <header className="border-b border-ink-200 bg-white">
        <Container width="full" className="py-4">
          <img src="/brand.png" alt="ParcelBuddy" className="h-14 w-auto sm:h-16" />
        </Container>
      </header>

      <Container width="full" className="py-8 sm:py-10">
        {/* Hero */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-800 tracking-tight text-ink-900 sm:text-3xl">
              Delivery issue reported
            </h1>
            <p className="mt-2 text-[15px] text-ink-600">
              {issueTypeLabels[caseData.issueType] || caseData.issueType} · Case {caseData.caseId}
            </p>
          </div>
          <StatusPill tone={statusTone}>{statusLabel}</StatusPill>
        </div>

        {/* Summary */}
        <Card className="mt-6 p-5">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {summaryRows.map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-ink-100 text-ink-600">
                  <Icon size={17} />
                </span>
                <div className="min-w-0">
                  <dt className="text-xs font-600 uppercase tracking-wide text-ink-500">{label}</dt>
                  <dd className="mt-0.5 break-words text-sm font-500 text-ink-900">{value}</dd>
                </div>
              </div>
            ))}
          </dl>
        </Card>

        <div className="mt-4 flex items-start gap-3 rounded-xl bg-ink-100 px-4 py-3.5">
          <IconLock size={17} className="mt-0.5 shrink-0 text-ink-500" />
          <p className="text-sm leading-relaxed text-ink-700">{gateMessage}</p>
        </div>

        <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
          {[
            'No login required',
            'No payment details shared',
            'Evidence provided upfront',
            'Read-only unless you take action'
          ].map((item) => (
            <li key={item} className="flex items-center gap-1.5 text-sm text-ink-600">
              <IconCheck size={14} className="shrink-0 text-brand-700" />
              {item}
            </li>
          ))}
        </ul>

        <p className="mt-4 text-sm leading-relaxed text-ink-600">
          ParcelBuddy helps merchants review delivery issues in one place — without back-and-forth
          emails.
        </p>

        {caseData.usage?.type === 'subscription' && (
          <p className="mt-4 text-sm text-ink-700">
            <span className="font-600">Usage this cycle:</span>{' '}
            {caseData.usage.includedCases == null
              ? 'Unlimited cases'
              : `${caseData.usage.remaining} of ${caseData.usage.includedCases} cases remaining`}
            {caseData.usage.cycleEnd && (
              <span className="text-ink-500">
                {' '}
                (resets {new Date(caseData.usage.cycleEnd).toLocaleDateString()})
              </span>
            )}
          </p>
        )}
        {caseData.usage?.type === 'pay_per_case' && (
          <p className="mt-4 text-sm text-ink-700">
            <span className="font-600">Pay-per-case:</span> {caseData.usage.payPerCaseCount} case
            {caseData.usage.payPerCaseCount !== 1 ? 's' : ''} this period
            {caseData.usage.payPerCaseWindowStart && (
              <span className="text-ink-500">
                {' '}
                (since {new Date(caseData.usage.payPerCaseWindowStart).toLocaleDateString()})
              </span>
            )}
          </p>
        )}
        {pilotUsage && (
          <p className="mt-4 text-sm text-ink-700">
            <span className="font-600">Pilot:</span>{' '}
            {pilotUsage.allowance != null
              ? `${pilotUsage.used} of ${pilotUsage.allowance} cases used`
              : `${pilotUsage.used} case${pilotUsage.used !== 1 ? 's' : ''} used`}
            {pilotUsage.endsAt && (
              <span className="text-ink-500">
                {' '}
                ({pilotEnded ? 'ended' : 'ends'} {new Date(pilotUsage.endsAt).toLocaleDateString()})
              </span>
            )}
          </p>
        )}

        {/*
          Every reason this modal can open (gated, pilot ended, or a new
          merchant who has chosen nothing yet) funnels through one
          `activationReason` so exactly one of the three ever shows — and so a
          new merchant sees the full pricing picture (pilot, plans, pay per
          case) rather than a single isolated button. None of the three is
          dismissable: choosing something is required before acting on any
          case, including the first.
        */}
        {showActivationModal && (
          <ActivationModal
            token={token}
            caseId={caseData.caseId}
            reason={activationReason}
            canStartPilot={activationReason === 'newMerchant' && canStartPilot}
            nextCycleAmount={activationReason === 'payPerCase' ? caseData?.nextCycleAmount : null}
            overageAmount={activationReason === 'quotaFilled' ? caseData?.overageAmount : null}
            currentPlan={m?.billingModel === 'subscription' ? m.plan : null}
            onActivated={(message) => {
              // Reached from the pilot path, and now also from switching an
              // existing subscription in place (no Stripe redirect either
              // way) — a fresh checkout to a brand-new Stripe subscription
              // still redirects and never calls this.
              stashToastAcrossReload(message);
              window.location.reload();
            }}
            onViewPricing={() => navigate('/pricing')}
          />
        )}

        {/* Case details */}
        <div className="mt-8 space-y-4">
          <Accordion
            icon={IconPackage}
            title="Delivery details + evidence"
            open={deliveryDetailsOpen}
            onToggle={() => setDeliveryDetailsOpen((open) => !open)}
          >
            <div className="space-y-4">
              <p className="font-display text-[15px] font-700 text-ink-900">
                {issueTypeLabels[caseData.issueType] || caseData.issueType}
              </p>

              {caseData.evidence?.comments && (
                <blockquote className="rounded-r-lg border-l-[3px] border-ink-300 bg-ink-50 px-4 py-3 text-sm italic leading-relaxed text-ink-700">
                  &ldquo;{caseData.evidence.comments}&rdquo;
                </blockquote>
              )}

              {caseData.dor?.signed && (
                <div className="flex items-start gap-2.5 rounded-lg bg-success-50 px-4 py-3 ring-1 ring-inset ring-success-100">
                  <IconNote size={17} className="mt-0.5 shrink-0 text-success-600" />
                  <p className="text-sm text-ink-700">
                    Declaration of Non-Receipt signed: {formatDateTime(caseData.dor.timestamp)}
                  </p>
                </div>
              )}

              <dl className="grid grid-cols-1 gap-3 border-t border-ink-100 pt-4 sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-600 uppercase tracking-wide text-ink-500">Tracking</dt>
                  <dd className="mt-0.5 text-sm text-ink-800">
                    {caseData.deliveryDetails.trackingNumber}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-600 uppercase tracking-wide text-ink-500">Courier</dt>
                  <dd className="mt-0.5 text-sm text-ink-800">{caseData.deliveryDetails.courier}</dd>
                </div>
                <div>
                  <dt className="text-xs font-600 uppercase tracking-wide text-ink-500">Order</dt>
                  <dd className="mt-0.5 text-sm text-ink-800">
                    #{caseData.deliveryDetails.orderNumber}
                  </dd>
                </div>
                {caseData.deliveryDetails.postcode && (
                  <div>
                    <dt className="text-xs font-600 uppercase tracking-wide text-ink-500">
                      Postcode
                    </dt>
                    <dd className="mt-0.5 text-sm text-ink-800">
                      {caseData.deliveryDetails.postcode}
                    </dd>
                  </div>
                )}
              </dl>

              {caseData.evidence?.files && caseData.evidence.files.length > 0 && (
                <div className="border-t border-ink-100 pt-4">
                  <p className="text-sm font-600 text-ink-900">Uploaded evidence</p>
                  <div className="mt-3 space-y-3">
                    {caseData.evidence.files.map((file, idx) => {
                      const isImage = file.mimetype && file.mimetype.startsWith('image/');
                      return (
                        <div
                          key={idx}
                          className="overflow-hidden rounded-xl border border-ink-200 bg-white"
                        >
                          <div className="flex items-center gap-2.5 border-b border-ink-200 px-3.5 py-2.5">
                            <IconNote size={16} className="shrink-0 text-ink-500" />
                            <span className="min-w-0 flex-1 truncate text-sm font-500 text-ink-900">
                              {file.filename}
                            </span>
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shrink-0 whitespace-nowrap text-sm font-600 text-brand-700 hover:text-brand-800 hover:underline"
                            >
                              {isImage ? 'Open full size' : 'Open file'}
                            </a>
                          </div>
                          {isImage && file.url && (
                            <div className="flex min-h-[120px] justify-center bg-ink-100 p-2">
                              <img
                                src={file.url}
                                alt={file.filename}
                                className="h-auto max-h-[280px] w-auto max-w-full rounded object-contain"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Accordion>

          {caseData.ship24?.events && caseData.ship24.events.length > 0 && (
            <Accordion
              icon={IconCourier}
              title="Courier tracking history (Ship24)"
              open={trackingHistoryOpen}
              onToggle={() => setTrackingHistoryOpen((open) => !open)}
            >
              <ol className="space-y-3">
                {caseData.ship24.events.map((event, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700">
                      <IconCheck size={13} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-500 text-ink-900">
                        {event.status}
                        {event.location ? ` — ${event.location}` : ''}
                      </p>
                      <p className="mt-0.5 text-xs text-ink-500">{formatDateTime(event.timestamp)}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </Accordion>
          )}

          {/* Customer details */}
          <Card className="p-5">
            <h2 className="font-display text-[15px] font-700 text-ink-900">Customer details</h2>
            <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-ink-100 text-ink-600">
                  <IconUser size={17} />
                </span>
                <div className="min-w-0">
                  <dt className="text-xs font-600 uppercase tracking-wide text-ink-500">Name</dt>
                  <dd className="mt-0.5 text-sm text-ink-900">{caseData.customer.name}</dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-ink-100 text-ink-600">
                  <IconMail size={17} />
                </span>
                <div className="min-w-0">
                  <dt className="text-xs font-600 uppercase tracking-wide text-ink-500">Email</dt>
                  <dd className="mt-0.5 break-words text-sm text-ink-900">
                    {caseData.customer.email}
                  </dd>
                </div>
              </div>
            </dl>
          </Card>

          {/* Evidence pack */}
          <Card className="p-5">
            <h2 className="font-display text-[15px] font-700 text-ink-900">Evidence pack</h2>
            {gated ? (
              <p className="mt-2 text-sm leading-relaxed text-ink-600">
                {limited
                  ? 'Settle any outstanding invoice to download the evidence pack.'
                  : awaitingReview
                    ? 'Pay for the next cycle to download the evidence pack.'
                    : pilotEnded
                      ? 'Your pilot has ended. Choose a plan to download the evidence pack.'
                      : 'Activate ParcelBuddy to download the evidence pack.'}
              </p>
            ) : (
              <>
                <p className="mt-2 text-sm leading-relaxed text-ink-600">
                  Everything the customer submitted, bundled as a single PDF.
                </p>
                <Button onClick={handleDownloadPDF} className="mt-4">
                  <IconDownload size={17} />
                  Download evidence pack PDF
                </Button>
              </>
            )}
          </Card>

          {/* Merchant actions */}
          {!caseData.merchant.action && !gated && (
            <Card className="p-5">
              <h2 className="font-display text-[15px] font-700 text-ink-900">Merchant actions</h2>
              <p className="mt-1.5 text-sm text-ink-600">
                Selecting an action will notify the customer automatically.
              </p>

              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/*
                  Each decision wears its own colour at card scale, not just on
                  the icon: green resolves, blue replaces, amber is still open
                  and waiting on the courier, red leaves the customer without
                  resolution. Class strings are written out in full because
                  Tailwind only emits what it can see literally.
                */}
                <button
                  type="button"
                  onClick={() => handleAction('refund')}
                  disabled={submitting}
                  className="flex items-center gap-3 rounded-xl border-2 border-success-100 bg-success-50/40 p-4 text-left transition-colors hover:border-success-600 hover:bg-success-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success-600 disabled:opacity-50"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-success-600 text-white">
                    <IconCheck size={19} />
                  </span>
                  <span className="font-display text-sm font-700 text-success-700">
                    Refund customer
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => handleAction('reship')}
                  disabled={submitting}
                  className="flex items-center gap-3 rounded-xl border-2 border-info-100 bg-info-50/40 p-4 text-left transition-colors hover:border-info-600 hover:bg-info-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-600 disabled:opacity-50"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-info-600 text-white">
                    <IconRefresh size={19} />
                  </span>
                  <span className="font-display text-sm font-700 text-info-700">Reship order</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleAction('escalate')}
                  disabled={submitting}
                  className="flex items-center gap-3 rounded-xl border-2 border-warning-100 bg-warning-50/40 p-4 text-left transition-colors hover:border-warning-600 hover:bg-warning-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning-600 disabled:opacity-50"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-warning-600 text-white">
                    <IconCourier size={19} />
                  </span>
                  <span className="font-display text-sm font-700 text-warning-700">
                    Escalate to courier
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setAction('reject')}
                  disabled={submitting}
                  className="flex items-center gap-3 rounded-xl border-2 border-danger-100 bg-danger-50/40 p-4 text-left transition-colors hover:border-danger-600 hover:bg-danger-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-600 disabled:opacity-50"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-danger-600 text-white">
                    <IconBan size={19} />
                  </span>
                  <span className="font-display text-sm font-700 text-danger-700">Reject claim</span>
                </button>
              </div>

              {action === 'reject' && (
                <div className="mt-5 border-t border-ink-100 pt-5">
                  <label
                    htmlFor="rejectionReason"
                    className="mb-1.5 block text-sm font-600 text-ink-800"
                  >
                    Rejection reason <span className="text-danger-600">*</span>
                  </label>
                  <Textarea
                    id="rejectionReason"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={4}
                    placeholder="Please provide a reason for rejecting this case…"
                  />
                  <div className="mt-4 flex flex-wrap gap-3">
                    <Button
                      variant="danger"
                      onClick={() => handleAction('reject')}
                      disabled={submitting || !rejectionReason.trim()}
                      loading={submitting}
                    >
                      Confirm rejection
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setAction(null);
                        setRejectionReason('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Gated – actions blocked. Pay-per-case's payPerCaseNeedsPayment and
              subscription's quotaFilled are both handled entirely by the
              ActivationModal above, so this alert's own awaiting_review
              branch is left for the cycle-level "pay for next cycle" state
              only (limited, pilot-ended, awaiting-activation, and a genuine
              awaiting_review status all still land here as before). */}
          {!caseData.merchant.action && gated && !payPerCaseNeedsPayment && !quotaFilled && (
            <Alert
              tone={limited ? 'danger' : awaitingReview ? 'info' : 'warning'}
              title={
                limited
                  ? 'Account limited'
                  : awaitingReview
                    ? 'Pay for next cycle'
                    : pilotEnded
                      ? 'Your pilot has ended'
                      : 'This case is awaiting activation'
              }
            >
              <p className="leading-relaxed">
                {limited
                  ? 'Settle any outstanding invoice to take action or download the evidence pack.'
                  : awaitingReview
                    ? 'Pay for the next billing cycle to take action or download the evidence pack.'
                    : 'Choose a plan above to take merchant actions.'}
              </p>

              {limited && caseData.unpaidInvoice && (
                <div className="mt-4">
                  <p className="text-sm">
                    Invoice {caseData.unpaidInvoice.invoiceId}: £
                    {Number(caseData.unpaidInvoice.amount).toFixed(2)}
                    {caseData.unpaidInvoice.dueAt && (
                      <span className="ml-1">
                        (due {new Date(caseData.unpaidInvoice.dueAt).toLocaleDateString()})
                      </span>
                    )}
                  </p>
                  <Button
                    onClick={handlePayInvoice}
                    disabled={payingInvoice}
                    loading={payingInvoice}
                    className="mt-3"
                  >
                    {payingInvoice ? 'Redirecting to payment…' : 'Pay invoice to continue'}
                  </Button>
                </div>
              )}

              {awaitingReview && caseData.nextCycleAmount != null && (
                <div className="mt-4">
                  <p className="text-sm">
                    Next cycle subscription: £{Number(caseData.nextCycleAmount).toFixed(2)}
                  </p>
                  <Button
                    onClick={handlePayNextCycle}
                    disabled={payingNextCycle}
                    loading={payingNextCycle}
                    className="mt-3"
                  >
                    {payingNextCycle ? 'Redirecting to payment…' : 'Pay next cycle to continue'}
                  </Button>
                </div>
              )}
            </Alert>
          )}

          {/* Action already taken */}
          {caseData.merchant.action && (
            <Alert tone="success" title="Action already taken">
              <p>
                {caseData.merchant.action.charAt(0).toUpperCase() + caseData.merchant.action.slice(1)}{' '}
                — {new Date(caseData.merchant.actionTakenAt).toLocaleString()}
              </p>
              {caseData.merchant.rejectionReason && (
                <p className="mt-2">
                  <span className="font-600">Reason:</span> {caseData.merchant.rejectionReason}
                </p>
              )}
            </Alert>
          )}
        </div>
      </Container>

      {/* Footer */}
      <footer className="mt-8 border-t border-ink-200 bg-white py-8">
        <Container width="full" className="text-center">
          <p className="text-sm text-ink-600">
            ParcelBuddy replaces delivery-issue emails with structured cases.
          </p>
          <p className="mt-3 text-sm text-ink-600">
            Not sure if this is legitimate?{' '}
            <a href="/verify" className="font-600 text-brand-700 underline hover:text-brand-800">
              Visit parcelbuddy.co.uk/verify and enter the case ID
            </a>
          </p>
          <SiteCredit className="mt-4" />
        </Container>
      </footer>
    </div>
  );
};

export default MerchantCasePage;
